import { useEffect, useState } from "react";
import { Button, Radio, Select, Switch } from "antd";
import { Modal, Table } from "../../../../../components/ui";
import { AssignOrganizerService } from "../../../../../apis/events";
import {
  GetOrganizerDropdown,
  toggleOrganizerStatus,
  UpdateOrganizerAccessLevel,
} from "../../../../../apis/organizers";
import { tostE, tostS } from "../../../../../config/toastConfig";
import OrganizerForm from "../../../../../components/organizers/OrganizerForm";

export const OrganizerDetailsCard = ({
  getEventDetails,
  id,
  loading,
  assignedOrganizer,
  access_level,
  setLoading,
}) => {
  const [showOrganizerModal, setShowOrganizerModal] = useState(false);
  const [isOrganizerUpdating, setIsOrganizerUpdating] = useState(false);
  const [organizerListOptions, setOrganizerListOptions] = useState([]);
  const [organizerList, setOrganizerList] = useState([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState({
    id: null,
    data: null,
  });
  const [mode, setMode] = useState("assign");

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile",
      key: "mobile",
      render: (_, record) =>
        `${record?.code ? record.code + " " : ""}${record?.mobile_number ?? ""}`,
    },
    ...(access_level === 1
      ? [
          {
            title: "Event Privileges",
            key: "access_level",
            render: (_, record) => (
              <Switch
                size="small"
                loading={loading}
                checked={record?.access_level === 1}
                onChange={(checked) =>
                  handleTogglePrivileges(record?.id, checked)
                }
              />
            ),
          },
        ]
      : []),
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Switch
          size="small"
          loading={loading}
          checked={record?.status === "Active"}
          onChange={async (checked) => {
            handleToggleStatus(record?.id, checked);
          }}
        />
      ),
    },
    // {
    //   title: "Action",
    //   dataIndex: "action",
    //   key: "action",
    // },
  ];

  const handleTogglePrivileges = async (id, checked) => {
    setLoading(true);
    try {
      // Update the access level: 1 = true, 0 = false
      const updated = {
        user_id: id,
        access_level: checked ? 1 : 0,
      };

      // You should create a new API like `UpdateOrganizerAccessLevelService`
      const res = await UpdateOrganizerAccessLevel(updated); // or UpdateOrganizerService(updated)

      if (res.status === 200 && res.data.success) {
        tostS(res.data.message);
        getEventDetails();
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.error(error);
      tostE(error.response.data.message);
    }
  };

  const handleToggleStatus = async (id, checked) => {
    setLoading(true);
    try {
      const res = await toggleOrganizerStatus(id, checked ? 1 : 0);

      if (res.status === 200 && res.data.success) {
        tostS(res.data.message);
        getEventDetails();
      } else {
        tostE(res.data.message);
      }
    } catch (error) {
      console.error(error);
      tostE(error.response.data.message);
    }
  };

  const handleChangeOrganizer = async (values) => {
    if (!values && !selectedOrganizer.id) {
      return;
    }
    const body = values
      ? { ...values, event_id: id }
      : { ...selectedOrganizer.data, event_id: id };
    setLoading(true);
    try {
      const response = await AssignOrganizerService(body);
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        getEventDetails();
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSelectedOrganizer({ id: null, data: null });
      setLoading(false);
      setShowOrganizerModal(false);
      setMode("assign");
    }
  };

  const getOrganizerDropdown = async () => {
    try {
      const res = await GetOrganizerDropdown();
      if (res.status === 200 && res.data.success) {
        setOrganizerList(res.data.organizer_dropdown_list);
        const mapped = [];
        for (const item of res.data.organizer_dropdown_list) {
          mapped.push({
            label: item.name
              ? `${item.name} (${item.code} ${item.mobile_number})`
              : item.code + " " + item.mobile_number,
            value: item.id,
          });
        }
        setOrganizerListOptions(mapped);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getOrganizerDropdown();
  }, []); // eslint-disable-line

  return (
    <>
      <div className="card custom-card my-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="">
            <h5 className="m-0 text-black">Organizer Details</h5>
            <p>Update Organizer Details</p>
          </div>
          <Button
            icon={<i className="fa-solid fa-plus"></i>}
            onClick={() => {
              setShowOrganizerModal(true);
            }}
          >
            Assign Organizer
          </Button>
        </div>
        <div>
          <Table loading={loading} data={assignedOrganizer} columns={columns} />
        </div>
      </div>
      <Modal
        open={showOrganizerModal}
        title="Update Organizer"
        confirmLoading={loading}
        handleCancel={() => {
          setShowOrganizerModal(false);
          setSelectedOrganizer({ id: null, data: null });
          setMode("assign");
        }}
        handleConfirm={() => {
          if (mode === "assign") {
            handleChangeOrganizer();
          }
        }}
        hideFooter={mode === "add"}
      >
        <div className="mb-3">
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Radio value="assign">Assign Organizer</Radio>
            <Radio value="add">Add New Organizer</Radio>
          </Radio.Group>
        </div>

        {mode === "assign" ? (
          <Select
            placeholder="Select Organizer"
            style={{ width: "100%" }}
            options={organizerListOptions}
            value={selectedOrganizer.id}
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            onChange={(val) => {
              setSelectedOrganizer({
                id: val,
                data: organizerList.find((item) => item.id === val),
              });
            }}
          />
        ) : (
          <OrganizerForm
            showAccessToggle={access_level === 1}
            onSubmit={handleChangeOrganizer}
            onCancel={() => {
              setShowOrganizerModal(false);
              setSelectedOrganizer({ id: null, data: null });
              setMode("assign");
            }}
          />
        )}
      </Modal>
    </>
  );
};
